import React, { useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  Updater,
} from '@tanstack/react-table';

import { DefaultTable } from '@hud/web-components/Table/Table';
import {
  renderDatetime,
  formatDatetime,
  renderLargeNumber,
} from '@hud/web-components/Table/CellRenderers';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import SearchRow from '@hud/web-components/SearchRow/SearchRow';

import { Service } from '@hud/portal-dtos/webapp-service.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import RunningStatus from '@hud/web-components/RunningStatus/RunningStatus';
import { DEFAULT_PAGE_SIZE } from '../../consts';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import tooltips from '../../assets/tooltips';
import NameCell from './NameCell';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import SelectMyServicesMenu from './SelectMyServicesMenu';
import MyServicesIcon from './MyServicesIcon';

const columnHelper = createColumnHelper<Service>();

export interface ServicesTableProps {
  services: Service[];
  total?: number;
  countActiveFiltered?: number;
  loading: boolean;

  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  userPreferredServices: number[];
  onSavePreferredServices: (ids: number[], isSingleService: boolean) => void;

  fullPageTable?: boolean;
}

export default function ServiceTable({
  services,
  loading,
  total = 0,
  countActiveFiltered = 0,
  searchValue,
  onSearchChange,
  sortingState,
  onSortingChange,
  pages,
  currentPage,
  onPageChange,
  userPreferredServices,
  onSavePreferredServices,
  fullPageTable,
}: ServicesTableProps) {
  const globalFilters = useGlobalQueryString();

  const columns = useMemo(() => {
    const columns = [
      columnHelper.display({
        id: 'myServices',
        header: () => <HudIcon icon="other.star" size="20px" />,
        cell: (cell) => (
          <MyServicesIcon
            id={cell.row.original.id}
            isPreferred={userPreferredServices.includes(cell.row.original.id)}
            onSelectService={onSavePreferredServices}
          />
        ),
        maxSize: 60,
        minSize: 60,
        size: 60,
        enableResizing: false,
        meta: {
          description: tooltips.SERVICES_TABLE.MY_SERVICES,
        },
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (cell) => (
          <NameCell.Cell
            to={`/explore/services/${cell.row.original.id}?` + globalFilters}
          >
            <NameCell.Runtime runtime={cell.row.original.runtime} />
            <NameCell.Name>{cell.getValue()}</NameCell.Name>
          </NameCell.Cell>
        ),
        minSize: 40,
        meta: {
          copyable: true,
          fitFormatter: (cell) => cell.getValue(),
          description: tooltips.SERVICES_TABLE.NAME,
        },
      }),
      columnHelper.accessor('state', {
        header: 'Status',
        cell: (cell) => <RunningStatus status={cell.getValue()} />,
        enableSorting: true,
        minSize: 40,
        meta: {
          description: tooltips.SERVICES_TABLE.STATE,
        },
      }),
      columnHelper.accessor('totalFunctions', {
        header: 'Functions mapped',
        cell: renderLargeNumber,
        minSize: 75,
        meta: {
          rightAlign: true,
          description: tooltips.SERVICES_TABLE.FUNCTIONS_MAPPED,
        },
      }),
      columnHelper.accessor('functionsInvoked', {
        header: 'Functions invoked',
        cell: renderLargeNumber,
        minSize: 75,
        meta: {
          rightAlign: true,
          description: tooltips.SERVICES_TABLE.FUNCTIONS_INVOKED,
        },
      }),
      columnHelper.accessor('lastSeenAt', {
        header: 'Last seen',
        cell: renderDatetime(true),
        minSize: 45,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.SERVICES_TABLE.LAST_SEEN,
        },
      }),
    ];
    return columns;
  }, [globalFilters, userPreferredServices, onSavePreferredServices]);

  const sortedServicesBySelected = useMemo(() => {
    services.sort((s1, s2) => {
      const inArray1 = userPreferredServices.includes(s1.id) ? 1 : 0;
      const inArray2 = userPreferredServices.includes(s2.id) ? 1 : 0;
      return inArray2 - inArray1;
    });
    return services;
  }, [services, userPreferredServices]);

  const allSelected = useMemo(() => {
    if (userPreferredServices.length !== services.length) return false;
    if (services.length === 0) return false;
    const selectedServiceIdsSet = new Set(userPreferredServices);
    return services.every((s) => selectedServiceIdsSet.has(s.id));
  }, [services, userPreferredServices]);

  const table = useReactTable({
    data: sortedServicesBySelected,
    columns: columns,
    state: {
      sorting: sortingState,
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onSortingChange,

    columnResizeMode: 'onChange',
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableSortingRemoval: false,

    defaultColumn: {},
  });

  return (
    <TableContainer.Container>
      <TableContainer.Header>
        <SearchRow>
          <Search inline value={searchValue} onSearchChange={onSearchChange} />
          {loading ? (
            <Skeleton width={100} />
          ) : (
            <ItemCounter
              total={total}
              filtered={countActiveFiltered}
              activeCounter={true}
            />
          )}
          <SelectMyServicesMenu
            allSelected={allSelected}
            serviceIds={services.map((s) => s.id)}
            onSelect={onSavePreferredServices}
          />
        </SearchRow>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Services Table"
          loading={loading}
          loadingRows={DEFAULT_PAGE_SIZE}
          options={{
            emptyStateProps: {
              icon: 'entities.servicesBlank',
              expanded: fullPageTable,
            },
          }}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={pages}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
